import { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useBiometricAuth } from '../hooks/useBiometricAuth';

export default function Login() {
  const { login, error, isLoading } = useAuth();
  const {
    isSupported: biometricSupported,
    isEnabled: biometricEnabled,
    isAuthenticating,
    error: biometricError,
    authenticateWithBiometric,
    checkBiometricAvailability,
  } = useBiometricAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [showBiometric, setShowBiometric] = useState(false);

  // Check if we should show biometric option
  useEffect(() => {
    async function checkBiometric() {
      if (biometricSupported && biometricEnabled) {
        const available = await checkBiometricAvailability();
        setShowBiometric(available);
      }
    }
    checkBiometric();
  }, [biometricSupported, biometricEnabled, checkBiometricAvailability]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    try {
      await login(email, password);
      // Navigation happens automatically via ProtectedRoute
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleBiometricLogin = async () => {
    setLocalError('');
    const success = await authenticateWithBiometric();

    if (success) {
      // Get saved credentials and login
      const savedEmail = localStorage.getItem('rdm_biometric_user_id');
      const savedToken = localStorage.getItem('accessToken');

      if (savedEmail && savedToken) {
        // Token still valid, trigger a re-check by refreshing
        window.location.reload();
      } else {
        setLocalError('Please sign in with your password to re-enable biometric login');
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 px-4 safe-area-inset-top safe-area-inset-bottom">
      {/* Logo/Brand */}
      <div className="mb-8 text-center">
        <div className="mx-auto mb-6 flex items-center justify-center rounded-2xl bg-white px-8 py-6 shadow-lg">
          <img
            src="/images/logo.png"
            alt="RDM Connect"
            className="h-28 w-auto object-contain"
          />
        </div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
        <h2 className="mb-6 text-center font-heading text-2xl font-semibold text-slate-800">
          Sign In
        </h2>

        {/* Error Display */}
        {(error || localError || biometricError) && (
          <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
            <div className="flex items-center">
              <svg
                className="mr-2 h-5 w-5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error || localError || biometricError}
            </div>
          </div>
        )}

        {/* Biometric Login Button */}
        {showBiometric && (
          <div className="mb-6">
            <button
              type="button"
              onClick={handleBiometricLogin}
              disabled={isAuthenticating || isLoading}
              className="flex w-full items-center justify-center gap-3 rounded-lg border-2 border-primary-600 bg-primary-50 px-4 py-3 font-semibold text-primary-700 transition-all hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
            >
              {isAuthenticating ? (
                <>
                  <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  {/* Fingerprint/Face ID Icon */}
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  <span>Sign in with Biometrics</span>
                </>
              )}
            </button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-slate-500">or sign in with email</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="mb-2 block text-sm font-medium text-slate-700"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="none"
              disabled={isLoading}
            />
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-medium text-slate-700"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white shadow-md transition-all hover:bg-primary-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="mr-2 h-5 w-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-500">
          RDM Merchandising
        </p>
      </div>

      {/* Version */}
      <p className="mt-8 text-center text-sm text-primary-200">
        <span>v{__APP_VERSION__}</span>
        <span className="mx-2">|</span>
        <span>{__GIT_COMMIT__}</span>
        <span className="mx-2">|</span>
        <span>
          {(() => {
            const date = new Date(__BUILD_DATE__);
            return isNaN(date.getTime()) ? __BUILD_DATE__ || 'N/A' : date.toLocaleDateString();
          })()}
        </span>
      </p>
    </div>
  );
}
